import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Check, MessageCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { BoardMember, KanbanCardComment } from 'types/kanban';
import { getCommentAttachmentDownloadUrl } from 'services/kanban';
import { getInitials } from '../../kanbanUtils';
import {
  useCurrentUserAvatar,
  isCurrentUser as isCurrentUserAvatar,
} from '../../useCurrentUserAvatar';
import {
  sanitizeCommentHtml,
  isCommentHtml,
  stripCommentHtmlImages,
} from 'utils/commentHtml';
import CommentEditorToolbar, {
  type CommentEditorToolbarActiveFormats,
} from '../CommentEditorToolbar';
import KanbanConfirmModal from '../../KanbanConfirmModal';
import styles from './CardDetailRightColumn.module.scss';

function parseSignedUrlExpiresAtMs(url: string): number | null {
  try {
    const parsed = new URL(url);
    const amzDate = parsed.searchParams.get('X-Amz-Date');
    const expires = parsed.searchParams.get('X-Amz-Expires');
    if (!amzDate || !expires) return null;
    const match = amzDate.match(
      /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/,
    );
    if (!match) return null;
    const [, y, m, d, hh, mm, ss] = match;
    const issuedAt = Date.UTC(
      Number(y),
      Number(m) - 1,
      Number(d),
      Number(hh),
      Number(mm),
      Number(ss),
    );
    const expiresMs = Number(expires) * 1000;
    if (!Number.isFinite(expiresMs) || expiresMs <= 0) return null;
    return issuedAt + expiresMs;
  } catch {
    return null;
  }
}

function CommentAttachmentMedia({
  boardId,
  cardId,
  path,
  filename,
  companyId,
  mimeType,
}: {
  boardId: string;
  cardId: string;
  path: string;
  filename: string;
  companyId: string | null;
  mimeType?: string;
}) {
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
  const [downloadUrlExpiresAtMs, setDownloadUrlExpiresAtMs] = useState<
    number | null
  >(null);
  const activeRequestRef = useRef(0);
  const retriedSourceRef = useRef<string | null>(null);

  const refreshDownloadUrl = useCallback(
    async (opts?: { preserveCurrentOnError?: boolean }) => {
      if (!boardId || !cardId || !companyId) return;
      const requestId = Date.now();
      activeRequestRef.current = requestId;
      try {
        const res = await getCommentAttachmentDownloadUrl(
          boardId,
          cardId,
          path,
          companyId,
        );
        if (activeRequestRef.current !== requestId) return;
        setDownloadUrl(res.downloadUrl);
        setDownloadUrlExpiresAtMs(parseSignedUrlExpiresAtMs(res.downloadUrl));
        retriedSourceRef.current = null;
      } catch {
        if (activeRequestRef.current !== requestId) return;
        if (!opts?.preserveCurrentOnError) {
          setDownloadUrl(null);
          setDownloadUrlExpiresAtMs(null);
        }
      }
    },
    [boardId, cardId, companyId, path],
  );

  useEffect(() => {
    void refreshDownloadUrl();
  }, [refreshDownloadUrl]);

  useEffect(() => {
    if (!downloadUrl || !downloadUrlExpiresAtMs) return;
    const refreshLeadMs = 2 * 60 * 1000; // Refresh 2 minutes before expiry.
    const now = Date.now();
    const msUntilRefresh = Math.max(
      5_000,
      downloadUrlExpiresAtMs - now - refreshLeadMs,
    );
    const timer = window.setTimeout(() => {
      void refreshDownloadUrl({ preserveCurrentOnError: true });
    }, msUntilRefresh);
    return () => window.clearTimeout(timer);
  }, [downloadUrl, downloadUrlExpiresAtMs, refreshDownloadUrl]);

  const handleMediaLoadError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement | HTMLVideoElement>) => {
      const mediaEl = e.currentTarget;
      const failedSrc = mediaEl.currentSrc || mediaEl.getAttribute('src') || '';
      if (!failedSrc) return;
      if (retriedSourceRef.current === failedSrc) return;
      retriedSourceRef.current = failedSrc;
      void refreshDownloadUrl({ preserveCurrentOnError: true });
    },
    [refreshDownloadUrl],
  );

  useEffect(() => {
    if (!isImagePreviewOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsImagePreviewOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isImagePreviewOpen]);

  if (!downloadUrl) return null;

  const isVideo = (() => {
    if (mimeType?.startsWith('video/')) return true;
    const lower = filename.toLowerCase();
    return /\.(webm|mp4|ogg|mov|m4v)$/i.test(lower);
  })();

  if (isVideo) {
    return (
      <video
        src={downloadUrl}
        controls
        preload="metadata"
        className={styles.commentAttachmentVideo}
        onError={handleMediaLoadError}
      />
    );
  }

  return (
    <>
      <img
        src={downloadUrl}
        alt={filename}
        className={styles.commentAttachmentImg}
        onError={handleMediaLoadError}
        onClick={() => setIsImagePreviewOpen(true)}
      />
      {isImagePreviewOpen && (
        <div
          className={styles.imagePreviewOverlay}
          role="dialog"
          aria-modal="true"
          aria-label={`Preview ${filename}`}
          onClick={() => setIsImagePreviewOpen(false)}
        >
          <button
            type="button"
            className={styles.imagePreviewCloseBtn}
            onClick={() => setIsImagePreviewOpen(false)}
            aria-label="Close image preview"
          >
            ×
          </button>
          <img
            src={downloadUrl}
            alt={filename}
            className={styles.imagePreviewImg}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}

export interface CardDetailRightColumnProps {
  boardId: string | null;
  cardId: string | null;
  companyId: string | null;
  boardMembers: BoardMember[];
  commentText: string;
  setCommentText: (v: string) => void;
  commentEditorActive: boolean;
  setCommentEditorActive: (v: boolean) => void;
  commentEditorRef: React.RefObject<HTMLDivElement | null>;
  commentEditRef: React.RefObject<HTMLDivElement | null>;
  editingCommentId: string | null;
  editingCommentText: string;
  setEditingCommentId: (v: string | null) => void;
  setEditingCommentText: (v: string) => void;
  onSubmitComment: (body?: string) => void;
  onSaveEditComment: (commentId: string, text?: string) => void;
  onDeleteComment: (commentId: string) => void;
  commentsNewestFirst: KanbanCardComment[];
  pendingCommentFiles: File[];
  onPendingFilesAdded: (files: File[]) => void;
  onClearPendingAttachments: () => void;
  isSubmittingComment: boolean;
  commentIdToScroll?: string | null;
  onCopyCommentLink?: (
    cardId: string,
    commentId: string,
  ) => void | Promise<void>;
}

export default function CardDetailRightColumn({
  boardId,
  cardId,
  companyId,
  boardMembers,
  commentText,
  setCommentText,
  commentEditorActive,
  setCommentEditorActive,
  commentEditorRef,
  commentEditRef,
  editingCommentId,
  editingCommentText,
  setEditingCommentId,
  setEditingCommentText,
  onSubmitComment,
  onSaveEditComment,
  onDeleteComment,
  commentsNewestFirst,
  pendingCommentFiles,
  onPendingFilesAdded,
  onClearPendingAttachments,
  isSubmittingComment,
  commentIdToScroll = null,
  onCopyCommentLink,
}: CardDetailRightColumnProps) {
  const { currentUserIds, profilePictureUrl } = useCurrentUserAvatar();
  const [copiedCommentId, setCopiedCommentId] = useState<string | null>(null);
  const [pendingDeleteCommentId, setPendingDeleteCommentId] = useState<
    string | null
  >(null);
  const newCommentContentRef = useRef<HTMLDivElement>(null);
  const editContentRef = useRef<HTMLDivElement>(null);
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const commentListRef = useRef<HTMLUListElement>(null);

  const [recordScreenOpen, setRecordScreenOpen] = useState(false);
  const [recordStage, setRecordStage] = useState<
    'idle' | 'requesting' | 'recording' | 'paused' | 'stopped'
  >('idle');
  const [recordError, setRecordError] = useState<string | null>(null);
  const [recordPreviewUrl, setRecordPreviewUrl] = useState<string | null>(null);
  const [recordedFile, setRecordedFile] = useState<File | null>(null);

  const mediaStreamRef = useRef<MediaStream | null>(null);
  const displayStreamRef = useRef<MediaStream | null>(null);
  const microphoneStreamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const recordedChunksRef = useRef<BlobPart[]>([]);
  const discardRecordingRef = useRef(false);

  const cleanupRecordResources = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop());
    }
    if (displayStreamRef.current) {
      displayStreamRef.current.getTracks().forEach((t) => t.stop());
    }
    if (microphoneStreamRef.current) {
      microphoneStreamRef.current.getTracks().forEach((t) => t.stop());
    }
    if (audioContextRef.current) {
      void audioContextRef.current.close().catch(() => {
        // Ignore cleanup failures for audio context.
      });
    }
    mediaStreamRef.current = null;
    displayStreamRef.current = null;
    microphoneStreamRef.current = null;
    mediaRecorderRef.current = null;
    audioContextRef.current = null;
    recordedChunksRef.current = [];
  }, []);

  const closeRecordModal = useCallback(() => {
    // If the recording already stopped and was attached, don't discard it.
    discardRecordingRef.current = recordStage !== 'stopped';
    setRecordScreenOpen(false);
    setRecordStage('idle');
    setRecordError(null);

    if (recordPreviewUrl) {
      URL.revokeObjectURL(recordPreviewUrl);
    }
    setRecordPreviewUrl(null);
    setRecordedFile(null);

    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      try {
        recorder.stop();
      } catch {
        // Ignore: we're discarding the recording.
      }
    } else {
      cleanupRecordResources();
    }
  }, [cleanupRecordResources, recordPreviewUrl, recordStage]);

  const startScreenRecording = useCallback(async () => {
    discardRecordingRef.current = false;
    setRecordError(null);
    setRecordStage('requesting');

    if (recordPreviewUrl) {
      URL.revokeObjectURL(recordPreviewUrl);
      setRecordPreviewUrl(null);
    }
    setRecordedFile(null);

    recordedChunksRef.current = [];

    const getDisplayMedia = navigator.mediaDevices?.getDisplayMedia?.bind(
      navigator.mediaDevices,
    );
    const getUserMedia = navigator.mediaDevices?.getUserMedia?.bind(
      navigator.mediaDevices,
    );
    if (!getDisplayMedia) {
      setRecordError('Screen recording is not supported in this browser.');
      setRecordStage('idle');
      return;
    }

    const inferMimeTypeFromRecorder = (mimeType: string): string => {
      if (mimeType.includes('mp4')) return 'video/mp4';
      if (mimeType.includes('webm')) return 'video/webm';
      return mimeType || 'video/webm';
    };

    const selectSupportedMimeType = (): string | undefined => {
      const candidates = [
        'video/webm;codecs=vp9',
        'video/webm;codecs=vp8',
        'video/webm',
      ];
      const isSupported = MediaRecorder.isTypeSupported?.bind(MediaRecorder);
      for (const c of candidates) {
        if (isSupported?.(c)) return c;
      }
      return undefined;
    };

    const shouldRetryWithoutSystemAudio = (err: unknown): boolean => {
      if (!(err instanceof DOMException)) return false;
      // User cancellation/denial should not trigger a second picker.
      if (err.name === 'NotAllowedError' || err.name === 'AbortError') {
        return false;
      }
      return true;
    };

    try {
      let displayStream: MediaStream;
      try {
        displayStream = await getDisplayMedia({ video: true, audio: true });
      } catch (error) {
        if (!shouldRetryWithoutSystemAudio(error)) throw error;
        // Some browsers can't capture system audio; retry without it.
        displayStream = await getDisplayMedia({ video: true, audio: false });
      }

      let microphoneStream: MediaStream | null = null;
      if (getUserMedia) {
        try {
          microphoneStream = await getUserMedia({ audio: true });
        } catch {
          // Continue without microphone audio if mic permission is denied.
          microphoneStream = null;
        }
      }

      const stream = new MediaStream(displayStream.getVideoTracks());
      const audioTracks = [
        ...displayStream.getAudioTracks(),
        ...(microphoneStream?.getAudioTracks() ?? []),
      ];

      if (audioTracks.length === 1) {
        stream.addTrack(audioTracks[0]);
      } else if (audioTracks.length > 1) {
        const AudioContextCtor =
          window.AudioContext ||
          (window as Window & { webkitAudioContext?: typeof AudioContext })
            .webkitAudioContext;
        if (AudioContextCtor) {
          const audioContext = new AudioContextCtor();
          const destination = audioContext.createMediaStreamDestination();

          for (const track of audioTracks) {
            const source = audioContext.createMediaStreamSource(
              new MediaStream([track]),
            );
            source.connect(destination);
          }
          destination.stream.getAudioTracks().forEach((track) => {
            stream.addTrack(track);
          });
          audioContextRef.current = audioContext;
        } else {
          stream.addTrack(audioTracks[0]);
        }
      }

      displayStreamRef.current = displayStream;
      microphoneStreamRef.current = microphoneStream;
      mediaStreamRef.current = stream;

      const mimeType = selectSupportedMimeType();
      const options: MediaRecorderOptions = {
        ...(mimeType ? { mimeType } : {}),
        videoBitsPerSecond: 2_500_000,
      };
      const recorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e: BlobEvent) => {
        if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const shouldDiscard = discardRecordingRef.current;
        const chunks = recordedChunksRef.current;
        cleanupRecordResources();

        if (shouldDiscard) return;
        const mimeType = inferMimeTypeFromRecorder(recorder.mimeType);
        const blob = new Blob(chunks, { type: mimeType });
        if (!blob.size) {
          setRecordError('Recording was empty. Please try again.');
          setRecordStage('idle');
          return;
        }

        const extension = mimeType.includes('mp4') ? 'mp4' : 'webm';
        const filename = `screen-recording-${new Date()
          .toISOString()
          .replace(/[:.]/g, '-')}.${extension}`;
        const file = new File([blob], filename, {
          type: blob.type || mimeType,
        });

        const url = URL.createObjectURL(blob);
        setRecordPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
        setRecordedFile(file);
        setRecordStage('stopped');
      };

      recorder.start();
      setRecordStage('recording');
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to start screen recording.';
      setRecordError(message);
      setRecordStage('idle');
      cleanupRecordResources();
    }
  }, [
    cleanupRecordResources,
    recordPreviewUrl,
    setRecordError,
    setRecordPreviewUrl,
  ]);

  const togglePauseResume = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return;

    if (recorder.state === 'recording') {
      try {
        recorder.pause();
        setRecordStage('paused');
      } catch {
        // Ignore.
      }
      return;
    }

    if (recorder.state === 'paused') {
      try {
        recorder.resume();
        setRecordStage('recording');
      } catch {
        // Ignore.
      }
    }
  }, []);

  const stopScreenRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return;
    if (recorder.state === 'inactive') return;
    try {
      recorder.stop();
    } catch {
      // Ignore.
    }
  }, []);

  const saveScreenRecording = useCallback(() => {
    if (!recordedFile) return;
    onPendingFilesAdded([recordedFile]);
    closeRecordModal();
  }, [closeRecordModal, onPendingFilesAdded, recordedFile]);

  const rerecordScreen = useCallback(() => {
    if (recordPreviewUrl) {
      URL.revokeObjectURL(recordPreviewUrl);
    }
    setRecordPreviewUrl(null);
    setRecordedFile(null);
    setRecordError(null);
    setRecordStage('idle');
    void startScreenRecording();
  }, [recordPreviewUrl, startScreenRecording]);

  const boardMemberMaps = useMemo(() => {
    const byUserId = new Map<string, BoardMember>();
    const byMemberId = new Map<string, BoardMember>();
    const byEmailLower = new Map<string, BoardMember>();
    const byNameLower = new Map<string, BoardMember>();

    boardMembers.forEach((m) => {
      byMemberId.set(m.id, m);
      if (m.userId) byUserId.set(m.userId, m);
      if (m.email) byEmailLower.set(m.email.toLowerCase(), m);
      if (m.name) byNameLower.set(m.name.toLowerCase(), m);
    });

    return { byUserId, byMemberId, byEmailLower, byNameLower };
  }, [boardMembers]);

  const copiedCommentTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const normalizeLegacyMemberActivityText = useCallback(
    (text: string, actorName: string): string => {
      const normalized = text.trim();
      if (/^added 1 member$/i.test(normalized)) {
        return `added member: ${actorName}`;
      }
      if (/^removed 1 member$/i.test(normalized)) {
        return `removed member: ${actorName}`;
      }
      return normalized;
    },
    [],
  );

  const isActivityByText = useCallback((text: string): boolean => {
    const normalized = text.trim().toLowerCase();
    return (
      normalized.startsWith('added member') ||
      normalized.startsWith('removed member') ||
      normalized.startsWith('renamed this card') ||
      normalized.startsWith('updated the description') ||
      normalized.startsWith('cleared the description') ||
      normalized.startsWith('deleted a comment') ||
      normalized.startsWith('deleted comment') ||
      normalized.startsWith('deleted activity item')
    );
  }, []);

  const handleCopyCommentLink = useCallback(
    async (commentId: string) => {
      if (!onCopyCommentLink || !cardId) return;
      try {
        await Promise.resolve(onCopyCommentLink(cardId, commentId));
        if (copiedCommentTimerRef.current) {
          clearTimeout(copiedCommentTimerRef.current);
        }
        setCopiedCommentId(commentId);
        copiedCommentTimerRef.current = setTimeout(() => {
          setCopiedCommentId(null);
          copiedCommentTimerRef.current = null;
        }, 2000);
      } catch {
        // Copy failed, don't show check
      }
    },
    [onCopyCommentLink, cardId],
  );

  useEffect(() => {
    if (!commentIdToScroll || !commentListRef.current) return;
    const el = commentListRef.current.querySelector(
      `[data-comment-id="${commentIdToScroll}"]`,
    );
    if (el) {
      const timer = setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [commentIdToScroll, commentsNewestFirst]);

  useEffect(() => {
    if (!pendingDeleteCommentId) return;
    const exists = commentsNewestFirst.some(
      (c) => c.id === pendingDeleteCommentId,
    );
    if (!exists) {
      setPendingDeleteCommentId(null);
    }
  }, [commentsNewestFirst, pendingDeleteCommentId]);
  const savedRangeRef = useRef<Range | null>(null);
  const savedEditorRef = useRef<HTMLDivElement | null>(null);
  const [activeFormats, setActiveFormats] =
    useState<CommentEditorToolbarActiveFormats>({
      bold: false,
      italic: false,
      strikethrough: false,
      code: false,
    });

  const isSelectionInsideCode = useCallback((): boolean => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return false;
    let node: Node | null = sel.anchorNode;
    while (node) {
      if (node instanceof Element && node.tagName === 'CODE') return true;
      node = node.parentNode;
    }
    return false;
  }, []);

  const getCodeElementContainingSelection = useCallback((): Element | null => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return null;
    let node: Node | null = sel.anchorNode;
    let codeEl: Element | null = null;
    while (node) {
      if (node instanceof Element && node.tagName === 'CODE') codeEl = node;
      node = node.parentNode;
    }
    return codeEl;
  }, []);

  const moveCursorAfterCodeElement = useCallback((): boolean => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return false;
    const codeEl = getCodeElementContainingSelection();
    if (!codeEl) return false;
    const boundaryAfter =
      codeEl.nextSibling instanceof HTMLElement &&
      codeEl.nextSibling.dataset?.codeBoundary === 'true'
        ? codeEl.nextSibling
        : null;
    const range = document.createRange();
    range.setStartAfter(boundaryAfter ?? codeEl);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
    return true;
  }, [getCodeElementContainingSelection]);

  const syncActiveFormatsFromDocument = useCallback(() => {
    const next = {
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      strikethrough: document.queryCommandState('strikeThrough'),
      code: isSelectionInsideCode(),
    };
    setActiveFormats((prev) =>
      prev.bold === next.bold &&
      prev.italic === next.italic &&
      prev.strikethrough === next.strikethrough &&
      prev.code === next.code
        ? prev
        : next,
    );
  }, [isSelectionInsideCode]);

  useEffect(() => {
    const onSelectionChange = () => {
      const activeEl = document.activeElement;
      if (
        activeEl !== newCommentContentRef.current &&
        activeEl !== editContentRef.current
      ) {
        // Avoid rerender-spam during selection outside the editor
        // (e.g. drag-selecting comment bubbles).
        setActiveFormats((prev) =>
          !prev.bold && !prev.italic && !prev.strikethrough && !prev.code
            ? prev
            : {
                bold: false,
                italic: false,
                strikethrough: false,
                code: false,
              },
        );
        return;
      }
      const next = {
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        strikethrough: document.queryCommandState('strikeThrough'),
        code: isSelectionInsideCode(),
      };
      setActiveFormats((prev) =>
        prev.bold === next.bold &&
        prev.italic === next.italic &&
        prev.strikethrough === next.strikethrough &&
        prev.code === next.code
          ? prev
          : next,
      );
    };
    document.addEventListener('selectionchange', onSelectionChange);
    return () =>
      document.removeEventListener('selectionchange', onSelectionChange);
  }, [isSelectionInsideCode]);

  useEffect(() => {
    if (commentText === '' && newCommentContentRef.current) {
      newCommentContentRef.current.innerHTML = '';
    }
  }, [commentText]);

  useEffect(() => {
    if (editingCommentId && editContentRef.current) {
      editContentRef.current.innerHTML = editingCommentText;
    }
  }, [editingCommentId, editingCommentText]);

  const handleNewCommentBold = useCallback(() => {
    newCommentContentRef.current?.focus();
    document.execCommand('bold', false, undefined);
    syncActiveFormatsFromDocument();
  }, [syncActiveFormatsFromDocument]);

  const handleNewCommentItalic = useCallback(() => {
    newCommentContentRef.current?.focus();
    document.execCommand('italic', false, undefined);
    syncActiveFormatsFromDocument();
  }, [syncActiveFormatsFromDocument]);

  const handleEditCommentBold = useCallback(() => {
    editContentRef.current?.focus();
    document.execCommand('bold', false, undefined);
    syncActiveFormatsFromDocument();
  }, [syncActiveFormatsFromDocument]);

  const handleEditCommentItalic = useCallback(() => {
    editContentRef.current?.focus();
    document.execCommand('italic', false, undefined);
    syncActiveFormatsFromDocument();
  }, [syncActiveFormatsFromDocument]);

  const restoreSelection = useCallback(() => {
    const editor = savedEditorRef.current;
    const range = savedRangeRef.current;
    if (!editor || !range) return;
    editor.focus();
    const sel = window.getSelection();
    if (!sel) return;
    sel.removeAllRanges();
    sel.addRange(range);
  }, []);

  const handleNewCommentSaveSelection = useCallback(() => {
    const sel = window.getSelection();
    savedRangeRef.current = sel?.rangeCount
      ? sel.getRangeAt(0).cloneRange()
      : null;
    savedEditorRef.current = newCommentContentRef.current;
  }, []);

  const handleEditSaveSelection = useCallback(() => {
    const sel = window.getSelection();
    savedRangeRef.current = sel?.rangeCount
      ? sel.getRangeAt(0).cloneRange()
      : null;
    savedEditorRef.current = editContentRef.current;
  }, []);

  const applyCodeToSelection = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    if (sel.isCollapsed && getCodeElementContainingSelection()) {
      moveCursorAfterCodeElement();
      return;
    }
    const text = sel.toString();
    const div = document.createElement('div');
    div.textContent = text;
    const escaped = div.innerHTML;
    document.execCommand(
      'insertHTML',
      false,
      text
        ? `<span data-code-boundary="true" contenteditable="false">\u200B</span><code>${escaped}</code><span data-code-boundary="true" contenteditable="false">\u200B</span>`
        : `<span data-code-boundary="true" contenteditable="false">\u200B</span><code>\u200B</code><span data-code-boundary="true" contenteditable="false">\u200B</span>`,
    );
    // Place cursor inside the inserted <code> so the user types in code format
    const anchor = sel.anchorNode;
    const anchorEl = (
      anchor?.nodeType === Node.TEXT_NODE ? anchor.parentElement : anchor
    ) as HTMLElement | null;

    const codeEl =
      getCodeElementContainingSelection() ??
      (anchorEl instanceof HTMLElement && anchorEl.tagName === 'CODE'
        ? anchorEl
        : anchorEl?.nextSibling instanceof HTMLElement &&
            anchorEl.nextSibling.tagName === 'CODE'
          ? anchorEl.nextSibling
          : anchorEl?.previousSibling instanceof HTMLElement &&
              anchorEl.previousSibling.tagName === 'CODE'
            ? anchorEl.previousSibling
            : anchorEl?.dataset?.codeBoundary === 'true' &&
                anchorEl.nextSibling instanceof HTMLElement &&
                anchorEl.nextSibling.tagName === 'CODE'
              ? anchorEl.nextSibling
              : null);

    if (codeEl instanceof HTMLElement && codeEl.tagName === 'CODE') {
      const range = document.createRange();
      const last = codeEl.lastChild;
      if (last) {
        if (last.nodeType === Node.TEXT_NODE) {
          range.setStart(last, (last.textContent ?? '').length);
        } else {
          range.setStartAfter(last);
        }
      } else {
        range.setStart(codeEl, 0);
      }
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }, [getCodeElementContainingSelection, moveCursorAfterCodeElement]);

  const handleNewCommentStrikethrough = useCallback(() => {
    restoreSelection();
    const isStrikeActive =
      activeFormats.strikethrough ||
      document.queryCommandState('strikeThrough');
    if (isStrikeActive) {
      // Toggle OFF
      document.execCommand('strikeThrough', false, undefined);
      setActiveFormats({
        bold: false,
        italic: false,
        strikethrough: false,
        code: false,
      });
      return;
    }
    // Normalize: clear other formats first (like "Clear formatting"),
    // then apply only strikethrough.
    const codeEl = getCodeElementContainingSelection();
    if (codeEl) {
      const text = codeEl.textContent ?? '';
      codeEl.replaceWith(document.createTextNode(text));
    }
    document.execCommand('removeFormat', false, undefined);
    if (document.queryCommandState('bold')) {
      document.execCommand('bold', false, undefined);
    }
    if (document.queryCommandState('italic')) {
      document.execCommand('italic', false, undefined);
    }
    if (document.queryCommandState('strikeThrough')) {
      document.execCommand('strikeThrough', false, undefined);
    }

    document.execCommand('strikeThrough', false, undefined);
    setActiveFormats({
      bold: false,
      italic: false,
      strikethrough: true,
      code: false,
    });
  }, [
    restoreSelection,
    getCodeElementContainingSelection,
    activeFormats.strikethrough,
  ]);

  const handleNewCommentCode = useCallback(() => {
    restoreSelection();
    const isCodeActive = activeFormats.code || isSelectionInsideCode();
    if (isCodeActive) {
      // Toggle OFF: do not unwrap existing <code>.
      // Just move the caret outside so subsequent typing is normal text.
      if (!moveCursorAfterCodeElement()) {
        // If we can't move (e.g. selection not collapsed), at least update toolbar state.
        setActiveFormats((prev) => ({
          ...prev,
          bold: false,
          italic: false,
          strikethrough: false,
          code: false,
        }));
      } else {
        setActiveFormats({
          bold: false,
          italic: false,
          strikethrough: false,
          code: false,
        });
      }
      return;
    }
    // Normalize: clear other formats first (like "Clear formatting"),
    // then apply only inline code.
    const codeEl = getCodeElementContainingSelection();
    if (codeEl) {
      const text = codeEl.textContent ?? '';
      codeEl.replaceWith(document.createTextNode(text));
    }
    document.execCommand('removeFormat', false, undefined);
    if (document.queryCommandState('bold')) {
      document.execCommand('bold', false, undefined);
    }
    if (document.queryCommandState('italic')) {
      document.execCommand('italic', false, undefined);
    }
    if (document.queryCommandState('strikeThrough')) {
      document.execCommand('strikeThrough', false, undefined);
    }

    applyCodeToSelection();
    setActiveFormats({
      bold: false,
      italic: false,
      strikethrough: false,
      code: true,
    });
  }, [
    restoreSelection,
    getCodeElementContainingSelection,
    applyCodeToSelection,
    activeFormats.code,
    isSelectionInsideCode,
    moveCursorAfterCodeElement,
  ]);

  const handleNewCommentClearFormatting = useCallback(() => {
    restoreSelection();
    // If inside inline code, exit it (don't unwrap/delete existing code).
    void moveCursorAfterCodeElement();
    document.execCommand('removeFormat', false, undefined);
    // `removeFormat` doesn't always clear toggled states depending on browser.
    if (document.queryCommandState('bold')) {
      document.execCommand('bold', false, undefined);
    }
    if (document.queryCommandState('italic')) {
      document.execCommand('italic', false, undefined);
    }
    if (document.queryCommandState('strikeThrough')) {
      document.execCommand('strikeThrough', false, undefined);
    }
    setActiveFormats({
      bold: false,
      italic: false,
      strikethrough: false,
      code: false,
    });
  }, [restoreSelection, moveCursorAfterCodeElement]);

  const handleEditCommentStrikethrough = useCallback(() => {
    restoreSelection();
    const isStrikeActive =
      activeFormats.strikethrough ||
      document.queryCommandState('strikeThrough');
    if (isStrikeActive) {
      document.execCommand('strikeThrough', false, undefined);
      setActiveFormats({
        bold: false,
        italic: false,
        strikethrough: false,
        code: false,
      });
      return;
    }
    const codeEl = getCodeElementContainingSelection();
    if (codeEl) {
      const text = codeEl.textContent ?? '';
      codeEl.replaceWith(document.createTextNode(text));
    }
    document.execCommand('removeFormat', false, undefined);
    if (document.queryCommandState('bold')) {
      document.execCommand('bold', false, undefined);
    }
    if (document.queryCommandState('italic')) {
      document.execCommand('italic', false, undefined);
    }
    if (document.queryCommandState('strikeThrough')) {
      document.execCommand('strikeThrough', false, undefined);
    }

    document.execCommand('strikeThrough', false, undefined);
    setActiveFormats({
      bold: false,
      italic: false,
      strikethrough: true,
      code: false,
    });
  }, [
    restoreSelection,
    getCodeElementContainingSelection,
    activeFormats.strikethrough,
  ]);

  const handleEditCommentCode = useCallback(() => {
    restoreSelection();
    const isCodeActive = activeFormats.code || isSelectionInsideCode();
    if (isCodeActive) {
      if (!moveCursorAfterCodeElement()) {
        setActiveFormats((prev) => ({
          ...prev,
          bold: false,
          italic: false,
          strikethrough: false,
          code: false,
        }));
      } else {
        setActiveFormats({
          bold: false,
          italic: false,
          strikethrough: false,
          code: false,
        });
      }
      return;
    }
    const codeEl = getCodeElementContainingSelection();
    if (codeEl) {
      const text = codeEl.textContent ?? '';
      codeEl.replaceWith(document.createTextNode(text));
    }
    document.execCommand('removeFormat', false, undefined);
    if (document.queryCommandState('bold')) {
      document.execCommand('bold', false, undefined);
    }
    if (document.queryCommandState('italic')) {
      document.execCommand('italic', false, undefined);
    }
    if (document.queryCommandState('strikeThrough')) {
      document.execCommand('strikeThrough', false, undefined);
    }

    applyCodeToSelection();
    setActiveFormats({
      bold: false,
      italic: false,
      strikethrough: false,
      code: true,
    });
  }, [
    restoreSelection,
    getCodeElementContainingSelection,
    applyCodeToSelection,
    activeFormats.code,
    isSelectionInsideCode,
    moveCursorAfterCodeElement,
  ]);

  const handleEditCommentClearFormatting = useCallback(() => {
    restoreSelection();
    void moveCursorAfterCodeElement();
    document.execCommand('removeFormat', false, undefined);
    if (document.queryCommandState('bold')) {
      document.execCommand('bold', false, undefined);
    }
    if (document.queryCommandState('italic')) {
      document.execCommand('italic', false, undefined);
    }
    if (document.queryCommandState('strikeThrough')) {
      document.execCommand('strikeThrough', false, undefined);
    }
    setActiveFormats({
      bold: false,
      italic: false,
      strikethrough: false,
      code: false,
    });
  }, [restoreSelection, moveCursorAfterCodeElement]);

  const handleNewCommentBulletList = useCallback(() => {
    newCommentContentRef.current?.focus();
    document.execCommand('insertUnorderedList', false, undefined);
    syncActiveFormatsFromDocument();
  }, [syncActiveFormatsFromDocument]);

  const handleNewCommentNumberedList = useCallback(() => {
    newCommentContentRef.current?.focus();
    document.execCommand('insertOrderedList', false, undefined);
    syncActiveFormatsFromDocument();
  }, [syncActiveFormatsFromDocument]);

  const handleEditCommentBulletList = useCallback(() => {
    editContentRef.current?.focus();
    document.execCommand('insertUnorderedList', false, undefined);
    syncActiveFormatsFromDocument();
  }, [syncActiveFormatsFromDocument]);

  const handleEditCommentNumberedList = useCallback(() => {
    editContentRef.current?.focus();
    document.execCommand('insertOrderedList', false, undefined);
    syncActiveFormatsFromDocument();
  }, [syncActiveFormatsFromDocument]);

  const createCommentEditorKeyDown = useCallback(
    (handlers: {
      onBold: () => void;
      onItalic: () => void;
      onStrikethrough: () => void;
      onCode: () => void;
      onClearFormatting: () => void;
      onBulletList: () => void;
      onNumberedList: () => void;
    }) =>
      (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) return;
        if (e.ctrlKey || e.metaKey) {
          if (e.shiftKey && e.key === 'S') {
            e.preventDefault();
            handlers.onStrikethrough();
            return;
          }
          if (e.shiftKey && e.key === 'M') {
            e.preventDefault();
            handlers.onCode();
            return;
          }
          if (e.key === '\\') {
            e.preventDefault();
            handlers.onClearFormatting();
            return;
          }
          if (e.shiftKey && e.key === '8') {
            e.preventDefault();
            handlers.onBulletList();
            return;
          }
          if (e.shiftKey && e.key === '7') {
            e.preventDefault();
            handlers.onNumberedList();
            return;
          }
          if (e.key === 'b') {
            e.preventDefault();
            handlers.onBold();
            return;
          }
          if (e.key === 'i') {
            e.preventDefault();
            handlers.onItalic();
            return;
          }
        }
      },
    [],
  );

  const getNewCommentBody = useCallback((): string => {
    const el = newCommentContentRef.current;
    if (!el) return commentText;
    const rawHtml = el.innerHTML ?? '';
    const rawText = el.innerText ?? '';
    const trimmedHtml = rawHtml.trim();
    const trimmedText = rawText.trim();

    // Preserve rich formatting when present (same approach as edit/save).
    if (trimmedHtml && isCommentHtml(trimmedHtml)) {
      return sanitizeCommentHtml(trimmedHtml).trim();
    }

    return trimmedText;
  }, [commentText]);

  const handleNewCommentPaste = useCallback(
    (e: React.ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const attachmentFiles: File[] = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (
          item.kind === 'file' &&
          (item.type.startsWith('image/') || item.type.startsWith('video/'))
        ) {
          const file = item.getAsFile();
          if (file) attachmentFiles.push(file);
        }
      }
      if (attachmentFiles.length > 0) {
        e.preventDefault();
        onPendingFilesAdded(attachmentFiles);
        const el = newCommentContentRef.current;
        if (el?.innerHTML) {
          const stripped = stripCommentHtmlImages(el.innerHTML);
          if (stripped !== el.innerHTML) {
            el.innerHTML = stripped;
            setCommentText(stripped);
          }
        }
      }
    },
    [onPendingFilesAdded, setCommentText],
  );

  const newCommentKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        onSubmitComment(getNewCommentBody());
        return;
      }
      createCommentEditorKeyDown({
        onBold: handleNewCommentBold,
        onItalic: handleNewCommentItalic,
        onStrikethrough: handleNewCommentStrikethrough,
        onCode: handleNewCommentCode,
        onClearFormatting: handleNewCommentClearFormatting,
        onBulletList: handleNewCommentBulletList,
        onNumberedList: handleNewCommentNumberedList,
      })(e);
    },
    [
      createCommentEditorKeyDown,
      getNewCommentBody,
      handleNewCommentBold,
      handleNewCommentItalic,
      handleNewCommentStrikethrough,
      handleNewCommentCode,
      handleNewCommentClearFormatting,
      handleNewCommentBulletList,
      handleNewCommentNumberedList,
      onSubmitComment,
    ],
  );

  const editCommentKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        const raw = editContentRef.current?.innerHTML ?? editingCommentText;
        onSaveEditComment(
          cachedEditingIdRef.current ?? '',
          sanitizeCommentHtml(raw),
        );
        return;
      }
      createCommentEditorKeyDown({
        onBold: handleEditCommentBold,
        onItalic: handleEditCommentItalic,
        onStrikethrough: handleEditCommentStrikethrough,
        onCode: handleEditCommentCode,
        onClearFormatting: handleEditCommentClearFormatting,
        onBulletList: handleEditCommentBulletList,
        onNumberedList: handleEditCommentNumberedList,
      })(e);
    },
    [
      createCommentEditorKeyDown,
      handleEditCommentBold,
      handleEditCommentItalic,
      handleEditCommentStrikethrough,
      handleEditCommentCode,
      handleEditCommentClearFormatting,
      handleEditCommentBulletList,
      handleEditCommentNumberedList,
      editingCommentText,
      onSaveEditComment,
    ],
  );

  // When Ctrl+Enter saves, we need to know which comment is being edited.
  const cachedEditingIdRef = useRef<string | null>(null);
  useEffect(() => {
    cachedEditingIdRef.current = editingCommentId;
  }, [editingCommentId]);

  return (
    <>
      <div className={styles.commentsHeader}>
        <h3 className={styles.modalSectionTitle}>
          <MessageCircle size={16} />
          Comments and activity
        </h3>
      </div>
      <div className={styles.commentInputWrap}>
        <div
          ref={commentEditorRef}
          className={`${styles.commentEditor} ${commentEditorActive ? styles.commentEditorActive : ''}`}
        >
          {commentEditorActive && (
            <CommentEditorToolbar
              onBold={handleNewCommentBold}
              onItalic={handleNewCommentItalic}
              onSaveSelection={handleNewCommentSaveSelection}
              onStrikethrough={handleNewCommentStrikethrough}
              onCode={handleNewCommentCode}
              onClearFormatting={handleNewCommentClearFormatting}
              onBulletList={handleNewCommentBulletList}
              onNumberedList={handleNewCommentNumberedList}
              activeFormats={activeFormats}
            />
          )}
          <div className={styles.commentInputBox}>
            <div
              ref={newCommentContentRef}
              className={styles.inputCommentNew}
              contentEditable
              suppressContentEditableWarning
              role="textbox"
              data-placeholder="Write a comment..."
              onInput={() => {
                setCommentText(newCommentContentRef.current?.innerHTML ?? '');
              }}
              onFocus={() => setCommentEditorActive(true)}
              onKeyDown={newCommentKeyDown}
              onPaste={handleNewCommentPaste}
              aria-label="Write a comment"
            />
          </div>
        </div>
        {commentEditorActive && (
          <div className={styles.commentNewActions}>
            <input
              ref={attachmentInputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                e.target.value = '';
                if (!files.length) return;
                onPendingFilesAdded(files);
                // Remove any image inserted into the input (e.g. by browser).
                const el = newCommentContentRef.current;
                if (el?.innerHTML) {
                  const stripped = stripCommentHtmlImages(el.innerHTML);
                  if (stripped !== el.innerHTML) {
                    el.innerHTML = stripped;
                    setCommentText(stripped);
                  }
                }
              }}
            />
            <button
              type="button"
              className={`${styles.formBtn} ${styles.secondary}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => attachmentInputRef.current?.click()}
              disabled={isSubmittingComment}
            >
              Add media
            </button>
            <button
              type="button"
              className={`${styles.formBtn} ${styles.secondary}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                if (recordStage !== 'idle') return;
                setRecordScreenOpen(true);
                void startScreenRecording();
              }}
              disabled={isSubmittingComment || recordStage !== 'idle'}
            >
              Record screen
            </button>
            {pendingCommentFiles.length > 0 && (
              <div className={styles.pendingAttachments}>
                {pendingCommentFiles.map((file, i) => (
                  <span
                    key={`${file.name}-${i}`}
                    className={styles.pendingAttachmentChip}
                  >
                    {file.name}
                  </span>
                ))}
              </div>
            )}
            <button
              type="button"
              className={`${styles.formBtn} ${styles.primary}`}
              onClick={() => {
                const body = getNewCommentBody();
                const hasContent = body || pendingCommentFiles.length > 0;
                if (hasContent) {
                  void onSubmitComment(body);
                }
              }}
              disabled={
                (!getNewCommentBody() && pendingCommentFiles.length === 0) ||
                isSubmittingComment
              }
            >
              {isSubmittingComment ? 'Posting…' : 'Add'}
            </button>
            <button
              type="button"
              className={`${styles.formBtn} ${styles.secondary}`}
              onClick={() => {
                setCommentText('');
                if (newCommentContentRef.current) {
                  newCommentContentRef.current.innerHTML = '';
                }
                onClearPendingAttachments();
                setCommentEditorActive(false);
              }}
              disabled={isSubmittingComment}
            >
              Cancel
            </button>
          </div>
        )}
      </div>
      <ul className={styles.activityList} ref={commentListRef}>
        {commentsNewestFirst.map((c) => {
          const isActivity =
            c.kind === 'activity' || isActivityByText(c.text ?? '');
          const isHighlighted =
            commentIdToScroll === c.id || copiedCommentId === c.id;
          const resolvedAuthorMember = c.authorId
            ? (boardMemberMaps.byUserId.get(c.authorId) ??
              boardMemberMaps.byMemberId.get(c.authorId) ??
              null)
            : null;
          const resolvedAuthorName =
            resolvedAuthorMember?.name?.trim() ||
            c.authorName?.trim() ||
            'Someone';
          const authorAvatarUrl = (() => {
            const authorNameLower = (c.authorName ?? '').trim().toLowerCase();
            if (c.authorId) {
              const byIdMatch =
                boardMemberMaps.byUserId.get(c.authorId) ??
                boardMemberMaps.byMemberId.get(c.authorId) ??
                null;
              if (byIdMatch?.profilePictureUrl)
                return byIdMatch.profilePictureUrl;
              // Back-compat: older comments may have an id that doesn't match
              // board member userId. Fall back to email/name matching.
              if (!authorNameLower) return null;
              return (
                boardMemberMaps.byEmailLower.get(authorNameLower)
                  ?.profilePictureUrl ??
                boardMemberMaps.byNameLower.get(authorNameLower)
                  ?.profilePictureUrl ??
                null
              );
            }
            if (!authorNameLower) return null;
            return (
              boardMemberMaps.byEmailLower.get(authorNameLower)
                ?.profilePictureUrl ??
              boardMemberMaps.byNameLower.get(authorNameLower)
                ?.profilePictureUrl ??
              null
            );
          })();
          const resolvedAvatarUrl =
            authorAvatarUrl ??
            (isCurrentUserAvatar(currentUserIds, c.authorId)
              ? profilePictureUrl
              : null);
          return (
            <li
              key={c.id}
              className={`${styles.activityItem} ${isActivity ? styles.activityEntry : ''} ${isHighlighted ? styles.activityItemHighlighted : ''}`}
              data-comment-id={c.id}
            >
              {resolvedAvatarUrl ? (
                <img
                  src={resolvedAvatarUrl}
                  alt=""
                  className={styles.activityAvatarImg}
                  aria-hidden
                />
              ) : (
                <span className={styles.activityAvatar}>
                  {getInitials(resolvedAuthorName || '?')}
                </span>
              )}
              <div className={styles.activityContent}>
                {!isActivity && (
                  <div className={styles.commentMeta}>
                    <span className={styles.activityAuthor}>
                      {resolvedAuthorName}
                    </span>
                    {onCopyCommentLink && cardId ? (
                      <span className={styles.commentMetaTimestamp}>
                        <button
                          type="button"
                          className={styles.activityDateButton}
                          onClick={() => void handleCopyCommentLink(c.id)}
                          title="Copy comment link"
                        >
                          {formatDistanceToNow(new Date(c.createdAt), {
                            addSuffix: true,
                          })}
                        </button>
                        {copiedCommentId === c.id && (
                          <Check
                            className={styles.copyCheckIcon}
                            size={14}
                            aria-label="Copied"
                          />
                        )}
                      </span>
                    ) : (
                      <span className={styles.activityDate}>
                        {formatDistanceToNow(new Date(c.createdAt), {
                          addSuffix: true,
                        })}
                      </span>
                    )}
                  </div>
                )}
                {editingCommentId === c.id ? (
                  <>
                    <div
                      className={styles.commentEditorToolbarWrap}
                      key="edit-toolbar-wrap"
                    >
                      <CommentEditorToolbar
                        onBold={handleEditCommentBold}
                        onItalic={handleEditCommentItalic}
                        onSaveSelection={handleEditSaveSelection}
                        onStrikethrough={handleEditCommentStrikethrough}
                        onCode={handleEditCommentCode}
                        onClearFormatting={handleEditCommentClearFormatting}
                        onBulletList={handleEditCommentBulletList}
                        onNumberedList={handleEditCommentNumberedList}
                        activeFormats={activeFormats}
                      />
                    </div>
                    <div
                      key="edit-field"
                      ref={(el) => {
                        editContentRef.current = el;
                        if (commentEditRef && 'current' in commentEditRef) {
                          (
                            commentEditRef as React.MutableRefObject<HTMLDivElement | null>
                          ).current = el;
                        }
                      }}
                      className={styles.commentBubbleEditable}
                      contentEditable
                      suppressContentEditableWarning
                      role="textbox"
                      aria-label="Edit comment"
                      onKeyDown={editCommentKeyDown}
                    />
                    <div
                      key="edit-actions"
                      className={styles.commentEditActions}
                    >
                      <button
                        type="button"
                        className={`${styles.formBtn} ${styles.primary}`}
                        onClick={() => {
                          const raw =
                            editContentRef.current?.innerHTML ??
                            editingCommentText;
                          onSaveEditComment(c.id, sanitizeCommentHtml(raw));
                        }}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className={`${styles.formBtn} ${styles.secondary}`}
                        onClick={() => {
                          setEditingCommentId(null);
                          setEditingCommentText('');
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {isActivity ? (
                      <div
                        key="activity-inline"
                        className={styles.activityInlineRow}
                      >
                        {(() => {
                          const activityText =
                            normalizeLegacyMemberActivityText(
                              c.text,
                              resolvedAuthorName,
                            );
                          return (
                            <>
                              {onCopyCommentLink && cardId ? (
                                <button
                                  type="button"
                                  className={styles.activityInlineAuthorButton}
                                  onClick={() =>
                                    void handleCopyCommentLink(c.id)
                                  }
                                  title="Copy comment link"
                                >
                                  {resolvedAuthorName}
                                </button>
                              ) : (
                                <span className={styles.activityInlineAuthor}>
                                  {resolvedAuthorName}
                                </span>
                              )}
                              <span className={styles.activityInlineText}>
                                {activityText}
                              </span>
                              <span className={styles.activityInlineTime}>
                                (
                                {formatDistanceToNow(new Date(c.createdAt), {
                                  addSuffix: true,
                                })}
                                )
                              </span>
                            </>
                          );
                        })()}
                      </div>
                    ) : (
                      <div
                        key="readonly-bubble"
                        className={styles.commentBubble}
                      >
                        {(c.text?.trim() ?? '') && (
                          <>
                            {c.text.includes('![') ? (
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                  a: ({ ...props }) => (
                                    <a
                                      {...props}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {props.children}
                                    </a>
                                  ),
                                }}
                              >
                                {(() => {
                                  const doc = new DOMParser().parseFromString(
                                    c.text,
                                    'text/html',
                                  );
                                  return doc.body.textContent ?? c.text;
                                })()}
                              </ReactMarkdown>
                            ) : isCommentHtml(c.text) ? (
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: sanitizeCommentHtml(c.text),
                                }}
                              />
                            ) : (
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                  a: ({ ...props }) => (
                                    <a
                                      {...props}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {props.children}
                                    </a>
                                  ),
                                }}
                              >
                                {c.text}
                              </ReactMarkdown>
                            )}
                          </>
                        )}
                        {boardId &&
                          cardId &&
                          c.attachments?.map((att, i) => (
                            <CommentAttachmentMedia
                              key={`${att.path}-${i}`}
                              boardId={boardId}
                              cardId={cardId}
                              path={att.path}
                              filename={att.filename}
                              companyId={companyId}
                              mimeType={att.mimeType}
                            />
                          ))}
                      </div>
                    )}
                    {!isActivity && (
                      <div
                        key="readonly-actions"
                        className={styles.commentActions}
                      >
                        <button
                          type="button"
                          className={styles.commentActionLink}
                          onClick={() => {
                            setEditingCommentId(c.id);
                            setEditingCommentText(c.text);
                          }}
                        >
                          Edit
                        </button>
                        <span className={styles.commentActionDot}>·</span>
                        <button
                          type="button"
                          className={styles.commentActionLink}
                          onClick={() => setPendingDeleteCommentId(c.id)}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {commentsNewestFirst.length === 0 && (
        <p className={styles.commentEmpty}>No comments yet.</p>
      )}

      {recordScreenOpen && (
        <div
          className={styles.recordOverlay}
          role="dialog"
          aria-modal="true"
          aria-label="Record screen"
        >
          <div className={styles.recordModal}>
            <div className={styles.recordHeader}>
              <h4 className={styles.recordTitle}>Record screen</h4>
              <button
                type="button"
                className={styles.recordCloseBtn}
                onClick={closeRecordModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {recordStage === 'requesting' && (
              <p className={styles.recordStatus}>Select a screen to record…</p>
            )}
            {recordStage === 'recording' && (
              <p className={styles.recordStatus}>Recording…</p>
            )}
            {recordStage === 'paused' && (
              <p className={styles.recordStatus}>Paused</p>
            )}

            {recordError && <p className={styles.recordError}>{recordError}</p>}

            {recordStage === 'stopped' && recordPreviewUrl && (
              <video
                className={styles.recordVideoPreview}
                controls
                preload="metadata"
                src={recordPreviewUrl}
              />
            )}

            <div className={styles.recordControls}>
              {(recordStage === 'recording' || recordStage === 'paused') && (
                <button
                  type="button"
                  className={`${styles.formBtn} ${styles.secondary}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={togglePauseResume}
                >
                  {recordStage === 'paused' ? 'Resume' : 'Pause'}
                </button>
              )}

              {(recordStage === 'recording' || recordStage === 'paused') && (
                <button
                  type="button"
                  className={`${styles.formBtn} ${styles.primary}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={stopScreenRecording}
                >
                  Stop
                </button>
              )}

              {recordStage === 'requesting' && (
                <button
                  type="button"
                  className={`${styles.formBtn} ${styles.secondary}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={closeRecordModal}
                >
                  Cancel
                </button>
              )}

              {(recordStage === 'recording' || recordStage === 'paused') && (
                <button
                  type="button"
                  className={`${styles.formBtn} ${styles.secondary}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={closeRecordModal}
                >
                  Cancel
                </button>
              )}

              {recordStage === 'stopped' && (
                <>
                  <button
                    type="button"
                    className={`${styles.formBtn} ${styles.primary}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={saveScreenRecording}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    className={`${styles.formBtn} ${styles.secondary}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={rerecordScreen}
                  >
                    Re-record
                  </button>
                  <button
                    type="button"
                    className={`${styles.formBtn} ${styles.secondary}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={closeRecordModal}
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>

            <p className={styles.recordHint}>
              After you stop, preview your recording then click{' '}
              <strong>Save</strong> to add it to this comment’s attachments.
            </p>
          </div>
        </div>
      )}
      <KanbanConfirmModal
        show={pendingDeleteCommentId != null}
        onHide={() => setPendingDeleteCommentId(null)}
        title="Delete comment"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={() => {
          if (!pendingDeleteCommentId) return;
          onDeleteComment(pendingDeleteCommentId);
          setPendingDeleteCommentId(null);
        }}
      >
        Are you sure you want to delete this comment?
      </KanbanConfirmModal>
    </>
  );
}
